// California Counties Data for County-Level Landing Pages
// 58 Counties organized by primary utility provider

export interface CaliforniaCounty {
  name: string;
  slug: string;
  primaryUtility: "PG&E" | "SCE" | "SDG&E" | "LADWP" | "SMUD" | "Multiple";
  population: string;
  majorCities: string[];
  avgSunnyDays: number;
  avgElectricBill: number;
  metaTitle: string;
  metaDescription: string;
  tldr: string;
  solarFacts: string[];
}

export const californiaCounties: CaliforniaCounty[] = [
  // Southern California Counties
  {
    name: "Los Angeles",
    slug: "solar-panels-los-angeles-county-ca",
    primaryUtility: "Multiple",
    population: "10.0 million",
    majorCities: ["Los Angeles", "Long Beach", "Glendale", "Santa Clarita", "Pasadena", "Pomona"],
    avgSunnyDays: 284,
    avgElectricBill: 285,
    metaTitle: "Solar Panels Los Angeles County 2026: $ Zero Down | 10M Residents | CEAG",
    metaDescription: "Los Angeles County solar installation in 2026. America's largest county. $Zero down PPA for LADWP, SCE customers. Save 70%+ on electricity.",
    tldr: "Los Angeles County is America's most populous county with 10 million residents. Multiple utilities serve the area (LADWP, SCE) with rates among the highest in the nation. Solar + battery storage is the proven solution for LA County energy independence.",
    solarFacts: [
      "10 million residents - largest county in the US",
      "284 sunny days per year - ideal solar production",
      "Multiple utilities: LADWP, SCE, various municipal",
      "Aggressive clean energy mandates drive solar adoption"
    ]
  },
  {
    name: "San Diego",
    slug: "solar-panels-san-diego-county-ca",
    primaryUtility: "SDG&E",
    population: "3.3 million",
    majorCities: ["San Diego", "Chula Vista", "Oceanside", "Escondido", "Carlsbad", "El Cajon"],
    avgSunnyDays: 266,
    avgElectricBill: 320,
    metaTitle: "Solar Panels San Diego County 2026: Escape SDG&E Rates | $ Zero Down | CEAG",
    metaDescription: "San Diego County has America's highest electricity rates. SDG&E customers save 75%+ with solar. $Zero down installation. Free quote.",
    tldr: "San Diego County residents pay the highest electricity rates in America through SDG&E. With 266 sunny days annually and extreme utility costs, solar + battery storage isn't optional—it's essential for financial survival.",
    solarFacts: [
      "SDG&E: highest residential rates in continental US",
      "3.3 million residents escaping rate hikes",
      "266 sunny days = exceptional solar production",
      "Battery storage critical for TOU peak rate avoidance"
    ]
  },
  {
    name: "Orange",
    slug: "solar-panels-orange-county-ca",
    primaryUtility: "SCE",
    population: "3.2 million",
    majorCities: ["Irvine", "Anaheim", "Santa Ana", "Huntington Beach", "Newport Beach", "Fullerton"],
    avgSunnyDays: 278,
    avgElectricBill: 265,
    metaTitle: "Solar Panels Orange County 2026: SCE Alternative | $ Zero Down | CEAG",
    metaDescription: "Orange County solar installation with $Zero down. SCE customers save 65%+. Irvine, Anaheim, coastal cities. Free consultation.",
    tldr: "Orange County's 3.2 million residents are served by SCE, whose rates increase 8%+ annually. With 278 sunny days and high home values, OC is a solar hotspot with excellent ROI.",
    solarFacts: [
      "3.2 million affluent residents",
      "SCE rates rising 8%+ annually",
      "278 sunny days per year",
      "High home values = excellent solar ROI"
    ]
  },
  {
    name: "Riverside",
    slug: "solar-panels-riverside-county-ca",
    primaryUtility: "SCE",
    population: "2.5 million",
    majorCities: ["Riverside", "Moreno Valley", "Corona", "Murrieta", "Temecula", "Menifee"],
    avgSunnyDays: 287,
    avgElectricBill: 255,
    metaTitle: "Solar Panels Riverside County 2026: Inland Empire | $ Zero Down | CEAG",
    metaDescription: "Riverside County solar installation. Inland Empire's hottest region. $Zero down. Save 68% on SCE bills. Free consultation.",
    tldr: "Riverside County's Inland Empire heat means sky-high AC bills. With 287 sunny days and extreme summer temperatures, solar + battery is the proven solution for the 2.5 million residents.",
    solarFacts: [
      "287 sunny days - among California's highest",
      "Extreme summer heat = high AC costs",
      "2.5 million residents in Inland Empire",
      "Fastest-growing county in California"
    ]
  },
  {
    name: "San Bernardino",
    slug: "solar-panels-san-bernardino-county-ca",
    primaryUtility: "SCE",
    population: "2.2 million",
    majorCities: ["San Bernardino", "Fontana", "Rancho Cucamonga", "Ontario", "Victorville", "Rialto"],
    avgSunnyDays: 290,
    avgElectricBill: 245,
    metaTitle: "Solar Panels San Bernardino County 2026: Inland Empire | $ Zero Down | CEAG",
    metaDescription: "San Bernardino County solar. Largest county in US by area. $Zero down installation. Save 70% on SCE. Free quote.",
    tldr: "San Bernardino County is the largest county in America by area, with 290 sunny days annually. The 2.2 million residents face extreme desert heat and rising SCE rates—solar is the only solution.",
    solarFacts: [
      "Largest county in America by land area",
      "290 sunny days - exceptional solar conditions",
      "Desert climate = extreme summer AC costs",
      "High desert communities ideal for solar"
    ]
  },
  {
    name: "Santa Clara",
    slug: "solar-panels-santa-clara-county-ca",
    primaryUtility: "PG&E",
    population: "1.9 million",
    majorCities: ["San Jose", "Sunnyvale", "Santa Clara", "Mountain View", "Palo Alto", "Cupertino"],
    avgSunnyDays: 257,
    avgElectricBill: 275,
    metaTitle: "Solar Panels Santa Clara County 2026: Silicon Valley | $ Zero Down | CEAG",
    metaDescription: "Santa Clara County solar. Silicon Valley's tech hub. $Zero down PPA. Beat PG&E rates. Free consultation for South Bay.",
    tldr: "Santa Clara County is the heart of Silicon Valley, where 1.9 million tech-forward residents are adopting solar at record rates. PG&E's rising rates and high home values make solar ROI exceptional.",
    solarFacts: [
      "Silicon Valley: tech capital of the world",
      "1.9 million tech-forward residents",
      "High EV adoption = solar + EV synergy",
      "Among highest home values in California"
    ]
  },
  {
    name: "Alameda",
    slug: "solar-panels-alameda-county-ca",
    primaryUtility: "PG&E",
    population: "1.7 million",
    majorCities: ["Oakland", "Fremont", "Hayward", "Berkeley", "San Leandro", "Livermore"],
    avgSunnyDays: 261,
    avgElectricBill: 235,
    metaTitle: "Solar Panels Alameda County 2026: East Bay Solar | $ Zero Down | CEAG",
    metaDescription: "Alameda County solar installation. Oakland, Fremont, East Bay. $Zero down. Cut PG&E bills 68%. Free consultation.",
    tldr: "Alameda County's 1.7 million East Bay residents are escaping PG&E's notorious rate hikes. From Oakland to Fremont, solar + battery provides energy independence and 68% average savings.",
    solarFacts: [
      "1.7 million East Bay residents",
      "PG&E PSPS outages drive battery demand",
      "Home to Tesla's Fremont factory",
      "Strong environmental communities"
    ]
  },
  {
    name: "Sacramento",
    slug: "solar-panels-sacramento-county-ca",
    primaryUtility: "SMUD",
    population: "1.6 million",
    majorCities: ["Sacramento", "Elk Grove", "Rancho Cordova", "Citrus Heights", "Folsom", "Carmichael"],
    avgSunnyDays: 269,
    avgElectricBill: 195,
    metaTitle: "Solar Panels Sacramento County 2026: SMUD Territory | $ Zero Down | CEAG",
    metaDescription: "Sacramento County solar. SMUD customers save 60%+. Capital region. $Zero down installation. Free consultation.",
    tldr: "Sacramento County is California's capital region, where 1.6 million residents enjoy SMUD's relatively reasonable rates—but extreme Central Valley heat still drives high bills. Solar slashes costs by 60%.",
    solarFacts: [
      "California's capital region",
      "SMUD: one of the best municipal utilities",
      "269 sunny days annually",
      "Extreme summer heat = high AC bills"
    ]
  },
  {
    name: "Contra Costa",
    slug: "solar-panels-contra-costa-county-ca",
    primaryUtility: "PG&E",
    population: "1.2 million",
    majorCities: ["Concord", "Richmond", "Antioch", "Walnut Creek", "San Ramon", "Pittsburg"],
    avgSunnyDays: 263,
    avgElectricBill: 255,
    metaTitle: "Solar Panels Contra Costa County 2026: East Bay | $ Zero Down | CEAG",
    metaDescription: "Contra Costa County solar. Walnut Creek, Concord, East Bay. $Zero down PPA. Beat PG&E rates. Free quote.",
    tldr: "Contra Costa County's 1.2 million residents span from the Delta to the hills. PG&E's aggressive rate increases make solar + battery essential for East Bay homeowners.",
    solarFacts: [
      "1.2 million East Bay residents",
      "Walnut Creek and Danville affluent communities",
      "Hot inland valleys ideal for solar",
      "PSPS outages drive battery adoption"
    ]
  },
  {
    name: "Fresno",
    slug: "solar-panels-fresno-county-ca",
    primaryUtility: "PG&E",
    population: "1.0 million",
    majorCities: ["Fresno", "Clovis", "Sanger", "Selma", "Coalinga", "Kerman"],
    avgSunnyDays: 271,
    avgElectricBill: 215,
    metaTitle: "Solar Panels Fresno County 2026: Central Valley | $ Zero Down | CEAG",
    metaDescription: "Fresno County solar installation. Central Valley's heart. $Zero down. Save 65% on PG&E. Free consultation.",
    tldr: "Fresno County is the agricultural heart of California's Central Valley. With 271 sunny days and extreme summer heat, the 1 million residents benefit enormously from solar + battery storage.",
    solarFacts: [
      "Agricultural capital of America",
      "271 sunny days annually",
      "Extreme Central Valley heat",
      "PG&E rates hitting rural communities hard"
    ]
  },
  {
    name: "Ventura",
    slug: "solar-panels-ventura-county-ca",
    primaryUtility: "SCE",
    population: "840,000",
    majorCities: ["Oxnard", "Thousand Oaks", "Simi Valley", "Ventura", "Camarillo", "Moorpark"],
    avgSunnyDays: 273,
    avgElectricBill: 245,
    metaTitle: "Solar Panels Ventura County 2026: Coastal Solar | $ Zero Down | CEAG",
    metaDescription: "Ventura County solar. Thousand Oaks, Simi Valley, Oxnard. $Zero down. Cut SCE bills. Free consultation.",
    tldr: "Ventura County bridges coastal beauty and inland heat. The 840,000 residents face rising SCE rates while enjoying 273 sunny days—perfect conditions for solar + battery.",
    solarFacts: [
      "Coastal and inland communities",
      "273 sunny days per year",
      "SCE rates rising 8%+ annually",
      "Wildfire risk drives battery demand"
    ]
  },
  {
    name: "San Francisco",
    slug: "solar-panels-san-francisco-county-ca",
    primaryUtility: "PG&E",
    population: "874,000",
    majorCities: ["San Francisco"],
    avgSunnyDays: 259,
    avgElectricBill: 245,
    metaTitle: "Solar Panels San Francisco County 2026: City Solar | $ Zero Down | CEAG",
    metaDescription: "San Francisco County solar. Urban solar solutions. $Zero down PPA. Cut PG&E bills 65%. Free consultation.",
    tldr: "San Francisco County's 874,000 residents face unique urban solar challenges—but with optimized system design, even foggy SF achieves 65% savings on PG&E. Battery backup protects against grid failures.",
    solarFacts: [
      "Unique urban solar challenges",
      "Fog-optimized system designs available",
      "Historic home solar solutions",
      "PG&E rates among highest in state"
    ]
  },
  {
    name: "San Mateo",
    slug: "solar-panels-san-mateo-county-ca",
    primaryUtility: "PG&E",
    population: "765,000",
    majorCities: ["Daly City", "San Mateo", "Redwood City", "South San Francisco", "Foster City", "Menlo Park"],
    avgSunnyDays: 261,
    avgElectricBill: 265,
    metaTitle: "Solar Panels San Mateo County 2026: Peninsula Solar | $ Zero Down | CEAG",
    metaDescription: "San Mateo County solar. Peninsula homeowners. $Zero down PPA. Beat PG&E rates. Free consultation.",
    tldr: "San Mateo County's 765,000 Peninsula residents have among California's highest home values. With PG&E's aggressive rates, solar ROI is exceptional—our $Zero down PPA starts saving immediately.",
    solarFacts: [
      "Among highest home values in California",
      "Tech executives going solar",
      "Peninsula climate ideal for production",
      "High EV adoption = solar synergy"
    ]
  },
  {
    name: "Kern",
    slug: "solar-panels-kern-county-ca",
    primaryUtility: "PG&E",
    population: "910,000",
    majorCities: ["Bakersfield", "Delano", "Wasco", "Ridgecrest", "Tehachapi", "Porterville"],
    avgSunnyDays: 272,
    avgElectricBill: 225,
    metaTitle: "Solar Panels Kern County 2026: Central Valley | $ Zero Down | CEAG",
    metaDescription: "Kern County solar. Bakersfield, Central Valley. $Zero down installation. Save 70% on PG&E. Free quote.",
    tldr: "Kern County is California's energy hub—home to oil, wind, and solar farms. The 910,000 residents enjoy 272 sunny days but face extreme heat. Residential solar cuts bills 70%.",
    solarFacts: [
      "California's energy production hub",
      "272 sunny days annually",
      "Extreme desert heat = high AC costs",
      "Wind and solar farms already thriving"
    ]
  },
  {
    name: "San Joaquin",
    slug: "solar-panels-san-joaquin-county-ca",
    primaryUtility: "PG&E",
    population: "780,000",
    majorCities: ["Stockton", "Tracy", "Manteca", "Lodi", "Lathrop", "Ripon"],
    avgSunnyDays: 266,
    avgElectricBill: 220,
    metaTitle: "Solar Panels San Joaquin County 2026: $ Zero Down | CEAG",
    metaDescription: "San Joaquin County solar. Stockton, Tracy, Manteca. $Zero down PPA. Cut PG&E bills. Free consultation.",
    tldr: "San Joaquin County's 780,000 residents in the Northern Central Valley face hot summers and rising PG&E rates. Solar + battery provides relief with 266 sunny days of production.",
    solarFacts: [
      "Northern Central Valley hub",
      "266 sunny days per year",
      "Growing Bay Area commuter population",
      "PG&E rates rising rapidly"
    ]
  },
  {
    name: "Stanislaus",
    slug: "solar-panels-stanislaus-county-ca",
    primaryUtility: "PG&E",
    population: "550,000",
    majorCities: ["Modesto", "Turlock", "Ceres", "Oakdale", "Riverbank", "Patterson"],
    avgSunnyDays: 268,
    avgElectricBill: 210,
    metaTitle: "Solar Panels Stanislaus County 2026: Modesto Area | $ Zero Down | CEAG",
    metaDescription: "Stanislaus County solar. Modesto, Turlock, Ceres. $Zero down installation. Beat PG&E. Free quote.",
    tldr: "Stanislaus County's 550,000 Central Valley residents experience extreme summer heat. With 268 sunny days and rising PG&E rates, solar is the proven path to lower bills.",
    solarFacts: [
      "Central Valley agricultural hub",
      "268 sunny days annually",
      "Extreme summer temperatures",
      "Affordable housing + solar = great ROI"
    ]
  },
  {
    name: "Tulare",
    slug: "solar-panels-tulare-county-ca",
    primaryUtility: "SCE",
    population: "475,000",
    majorCities: ["Visalia", "Tulare", "Porterville", "Dinuba", "Lindsay", "Exeter"],
    avgSunnyDays: 270,
    avgElectricBill: 205,
    metaTitle: "Solar Panels Tulare County 2026: Central Valley | $ Zero Down | CEAG",
    metaDescription: "Tulare County solar. Visalia, Porterville. $Zero down PPA. Cut SCE bills. Free consultation.",
    tldr: "Tulare County's 475,000 residents in the Southern Central Valley face hot summers and SCE rate increases. With 270 sunny days, solar + battery is the smart investment.",
    solarFacts: [
      "Southern Central Valley",
      "270 sunny days per year",
      "Agricultural economy",
      "SCE rates increasing annually"
    ]
  },
  {
    name: "Sonoma",
    slug: "solar-panels-sonoma-county-ca",
    primaryUtility: "PG&E",
    population: "490,000",
    majorCities: ["Santa Rosa", "Petaluma", "Rohnert Park", "Windsor", "Healdsburg", "Sonoma"],
    avgSunnyDays: 256,
    avgElectricBill: 235,
    metaTitle: "Solar Panels Sonoma County 2026: Wine Country | $ Zero Down | CEAG",
    metaDescription: "Sonoma County solar. Santa Rosa, Petaluma, wine country. $Zero down. Beat PG&E. Free consultation.",
    tldr: "Sonoma County's 490,000 Wine Country residents face frequent PG&E PSPS outages due to wildfire risk. Solar + battery provides energy independence and blackout protection.",
    solarFacts: [
      "Wine Country communities",
      "High PSPS outage risk area",
      "Battery storage essential",
      "Environmental communities embracing solar"
    ]
  },
  {
    name: "Solano",
    slug: "solar-panels-solano-county-ca",
    primaryUtility: "PG&E",
    population: "450,000",
    majorCities: ["Vallejo", "Fairfield", "Vacaville", "Suisun City", "Benicia", "Dixon"],
    avgSunnyDays: 262,
    avgElectricBill: 225,
    metaTitle: "Solar Panels Solano County 2026: North Bay | $ Zero Down | CEAG",
    metaDescription: "Solano County solar. Vallejo, Fairfield, Vacaville. $Zero down PPA. Cut PG&E bills. Free quote.",
    tldr: "Solano County's 450,000 residents bridge the Bay Area and Central Valley. With 262 sunny days and hot Vacaville summers, solar + battery cuts PG&E bills dramatically.",
    solarFacts: [
      "Bridge between Bay Area and Central Valley",
      "262 sunny days annually",
      "Affordable compared to SF Bay Area",
      "Wind country = unique solar optimization"
    ]
  },
  {
    name: "Santa Barbara",
    slug: "solar-panels-santa-barbara-county-ca",
    primaryUtility: "SCE",
    population: "450,000",
    majorCities: ["Santa Barbara", "Santa Maria", "Lompoc", "Goleta", "Carpinteria", "Solvang"],
    avgSunnyDays: 283,
    avgElectricBill: 235,
    metaTitle: "Solar Panels Santa Barbara County 2026: Central Coast | $ Zero Down | CEAG",
    metaDescription: "Santa Barbara County solar. Coastal beauty + solar savings. $Zero down. Cut SCE bills. Free consultation.",
    tldr: "Santa Barbara County's 450,000 residents enjoy 283 sunny days on the Central Coast. With SCE's rising rates, solar + battery provides both savings and coastal resilience.",
    solarFacts: [
      "283 sunny days - among CA's best",
      "Central Coast paradise",
      "SCE rates climbing annually",
      "High home values = excellent ROI"
    ]
  },
  {
    name: "Monterey",
    slug: "solar-panels-monterey-county-ca",
    primaryUtility: "PG&E",
    population: "440,000",
    majorCities: ["Salinas", "Monterey", "Seaside", "Marina", "Pacific Grove", "Carmel"],
    avgSunnyDays: 267,
    avgElectricBill: 225,
    metaTitle: "Solar Panels Monterey County 2026: Central Coast | $ Zero Down | CEAG",
    metaDescription: "Monterey County solar. Salinas, Monterey, Carmel. $Zero down PPA. Beat PG&E rates. Free consultation.",
    tldr: "Monterey County's 440,000 residents span from coastal Carmel to agricultural Salinas. PG&E serves both, and solar + battery provides savings across the diverse landscape.",
    solarFacts: [
      "Coastal and agricultural regions",
      "267 sunny days per year",
      "Salinas Valley: America's Salad Bowl",
      "PG&E rates rising for all"
    ]
  },
  {
    name: "Placer",
    slug: "solar-panels-placer-county-ca",
    primaryUtility: "PG&E",
    population: "410,000",
    majorCities: ["Roseville", "Rocklin", "Lincoln", "Auburn", "Loomis", "Granite Bay"],
    avgSunnyDays: 269,
    avgElectricBill: 255,
    metaTitle: "Solar Panels Placer County 2026: Sacramento Suburbs | $ Zero Down | CEAG",
    metaDescription: "Placer County solar. Roseville, Rocklin, Lincoln. $Zero down installation. Beat PG&E. Free quote.",
    tldr: "Placer County's 410,000 residents in Sacramento's eastern suburbs enjoy excellent solar conditions. From Roseville to Auburn, PG&E's high rates make solar + battery a no-brainer.",
    solarFacts: [
      "Sacramento's affluent eastern suburbs",
      "269 sunny days annually",
      "Growing population = new construction solar",
      "PSPS outages in foothill communities"
    ]
  },
  {
    name: "San Luis Obispo",
    slug: "solar-panels-san-luis-obispo-county-ca",
    primaryUtility: "PG&E",
    population: "280,000",
    majorCities: ["San Luis Obispo", "Paso Robles", "Atascadero", "Arroyo Grande", "Pismo Beach", "Morro Bay"],
    avgSunnyDays: 275,
    avgElectricBill: 225,
    metaTitle: "Solar Panels San Luis Obispo County 2026: Central Coast | $ Zero Down | CEAG",
    metaDescription: "SLO County solar. Paso Robles, SLO, coast. $Zero down PPA. Beat PG&E rates. Free consultation.",
    tldr: "San Luis Obispo County's 280,000 Central Coast residents enjoy 275 sunny days. From wine country Paso Robles to coastal SLO, solar + battery provides independence from PG&E.",
    solarFacts: [
      "275 sunny days - exceptional conditions",
      "Central Coast wine country",
      "Cal Poly: clean energy education hub",
      "PG&E rates driving solar adoption"
    ]
  },
  {
    name: "Marin",
    slug: "solar-panels-marin-county-ca",
    primaryUtility: "PG&E",
    population: "260,000",
    majorCities: ["San Rafael", "Novato", "Mill Valley", "Larkspur", "Sausalito", "Tiburon"],
    avgSunnyDays: 260,
    avgElectricBill: 275,
    metaTitle: "Solar Panels Marin County 2026: North Bay | $ Zero Down | CEAG",
    metaDescription: "Marin County solar. Mill Valley, San Rafael, Sausalito. $Zero down. Cut PG&E bills. Free consultation.",
    tldr: "Marin County's 260,000 affluent residents have among California's highest incomes and strongest environmental values. Solar + battery aligns with Marin's sustainability leadership.",
    solarFacts: [
      "Among California's wealthiest counties",
      "Strong environmental leadership",
      "MCE: community choice energy partner",
      "PSPS outages drive battery demand"
    ]
  },
  {
    name: "Santa Cruz",
    slug: "solar-panels-santa-cruz-county-ca",
    primaryUtility: "PG&E",
    population: "270,000",
    majorCities: ["Santa Cruz", "Watsonville", "Scotts Valley", "Capitola", "Aptos", "Soquel"],
    avgSunnyDays: 263,
    avgElectricBill: 235,
    metaTitle: "Solar Panels Santa Cruz County 2026: Coastal | $ Zero Down | CEAG",
    metaDescription: "Santa Cruz County solar. UCSC area, Watsonville. $Zero down PPA. Beat PG&E rates. Free quote.",
    tldr: "Santa Cruz County's 270,000 residents blend coastal lifestyle with environmental consciousness. PG&E's rising rates and wildfire risks make solar + battery the smart choice.",
    solarFacts: [
      "Strong environmental community",
      "UCSC: clean energy research hub",
      "Coastal and mountain communities",
      "Wildfire risk drives battery adoption"
    ]
  },
  {
    name: "Merced",
    slug: "solar-panels-merced-county-ca",
    primaryUtility: "PG&E",
    population: "290,000",
    majorCities: ["Merced", "Los Banos", "Atwater", "Livingston", "Gustine", "Winton"],
    avgSunnyDays: 268,
    avgElectricBill: 205,
    metaTitle: "Solar Panels Merced County 2026: Central Valley | $ Zero Down | CEAG",
    metaDescription: "Merced County solar. UC Merced area. $Zero down installation. Cut PG&E bills. Free consultation.",
    tldr: "Merced County's 290,000 Central Valley residents face extreme summer heat. With UC Merced leading clean energy research, solar + battery adoption is accelerating.",
    solarFacts: [
      "UC Merced: clean energy research",
      "268 sunny days annually",
      "Affordable housing + solar = great ROI",
      "Extreme Central Valley heat"
    ]
  },
  {
    name: "Butte",
    slug: "solar-panels-butte-county-ca",
    primaryUtility: "PG&E",
    population: "210,000",
    majorCities: ["Chico", "Paradise", "Oroville", "Gridley", "Biggs", "Durham"],
    avgSunnyDays: 265,
    avgElectricBill: 215,
    metaTitle: "Solar Panels Butte County 2026: Chico Area | $ Zero Down | CEAG",
    metaDescription: "Butte County solar. Chico, Paradise rebuilding. $Zero down PPA. Beat PG&E. Free consultation.",
    tldr: "Butte County's 210,000 residents know wildfire devastation firsthand. Paradise is rebuilding with solar + battery as essential infrastructure for energy resilience.",
    solarFacts: [
      "Paradise rebuilding with solar priority",
      "High PSPS outage risk area",
      "Battery storage essential",
      "Chico State environmental leadership"
    ]
  },
  {
    name: "Yolo",
    slug: "solar-panels-yolo-county-ca",
    primaryUtility: "PG&E",
    population: "220,000",
    majorCities: ["Davis", "Woodland", "West Sacramento", "Winters", "Knights Landing"],
    avgSunnyDays: 269,
    avgElectricBill: 225,
    metaTitle: "Solar Panels Yolo County 2026: Davis Area | $ Zero Down | CEAG",
    metaDescription: "Yolo County solar. Davis, Woodland, West Sacramento. $Zero down. Beat PG&E rates. Free quote.",
    tldr: "Yolo County's 220,000 residents include UC Davis—America's top agricultural university. The campus leads solar research while homeowners escape PG&E with rooftop systems.",
    solarFacts: [
      "UC Davis: solar research leader",
      "269 sunny days per year",
      "Environmental community in Davis",
      "PG&E rates driving adoption"
    ]
  },
  {
    name: "El Dorado",
    slug: "solar-panels-el-dorado-county-ca",
    primaryUtility: "PG&E",
    population: "195,000",
    majorCities: ["El Dorado Hills", "Placerville", "Cameron Park", "South Lake Tahoe", "Shingle Springs"],
    avgSunnyDays: 267,
    avgElectricBill: 245,
    metaTitle: "Solar Panels El Dorado County 2026: Foothill | $ Zero Down | CEAG",
    metaDescription: "El Dorado County solar. El Dorado Hills, Placerville. $Zero down PPA. Beat PG&E. Free consultation.",
    tldr: "El Dorado County's 195,000 residents span from the Sacramento suburbs to Lake Tahoe. PSPS outages and wildfire risk make solar + battery essential for foothill communities.",
    solarFacts: [
      "Foothill and mountain communities",
      "High PSPS outage frequency",
      "Battery storage critical",
      "Affluent El Dorado Hills market"
    ]
  },
  {
    name: "Napa",
    slug: "solar-panels-napa-county-ca",
    primaryUtility: "PG&E",
    population: "140,000",
    majorCities: ["Napa", "American Canyon", "St. Helena", "Calistoga", "Yountville"],
    avgSunnyDays: 264,
    avgElectricBill: 255,
    metaTitle: "Solar Panels Napa County 2026: Wine Country | $ Zero Down | CEAG",
    metaDescription: "Napa County solar. Wine country solar solutions. $Zero down. Beat PG&E rates. Free consultation.",
    tldr: "Napa County's 140,000 Wine Country residents face frequent PSPS outages due to extreme fire risk. Solar + battery provides both savings and critical backup power.",
    solarFacts: [
      "World-famous Wine Country",
      "Extreme wildfire risk area",
      "Battery storage essential",
      "High home values = excellent ROI"
    ]
  },
  {
    name: "Kings",
    slug: "solar-panels-kings-county-ca",
    primaryUtility: "PG&E",
    population: "155,000",
    majorCities: ["Hanford", "Lemoore", "Avenal", "Corcoran"],
    avgSunnyDays: 269,
    avgElectricBill: 195,
    metaTitle: "Solar Panels Kings County 2026: Central Valley | $ Zero Down | CEAG",
    metaDescription: "Kings County solar. Hanford, Lemoore. $Zero down installation. Cut PG&E bills. Free quote.",
    tldr: "Kings County's 155,000 Central Valley residents experience extreme heat and rising PG&E rates. Solar + battery provides relief with 269 sunny days of production.",
    solarFacts: [
      "Central Valley agricultural hub",
      "269 sunny days per year",
      "Affordable housing market",
      "Extreme summer heat"
    ]
  },
  {
    name: "Madera",
    slug: "solar-panels-madera-county-ca",
    primaryUtility: "PG&E",
    population: "160,000",
    majorCities: ["Madera", "Chowchilla", "Oakhurst"],
    avgSunnyDays: 270,
    avgElectricBill: 200,
    metaTitle: "Solar Panels Madera County 2026: Yosemite Gateway | $ Zero Down | CEAG",
    metaDescription: "Madera County solar. Gateway to Yosemite. $Zero down PPA. Beat PG&E rates. Free consultation.",
    tldr: "Madera County's 160,000 residents live at the gateway to Yosemite. From the Central Valley floor to mountain communities, solar + battery provides energy independence.",
    solarFacts: [
      "Gateway to Yosemite National Park",
      "270 sunny days annually",
      "Central Valley heat",
      "Mountain communities need battery backup"
    ]
  },
  {
    name: "Shasta",
    slug: "solar-panels-shasta-county-ca",
    primaryUtility: "PG&E",
    population: "180,000",
    majorCities: ["Redding", "Anderson", "Shasta Lake"],
    avgSunnyDays: 265,
    avgElectricBill: 225,
    metaTitle: "Solar Panels Shasta County 2026: Northern CA | $ Zero Down | CEAG",
    metaDescription: "Shasta County solar. Redding area. $Zero down installation. Beat PG&E rates. Free quote.",
    tldr: "Shasta County's 180,000 residents experience California's hottest summers in Redding. With extreme heat and PSPS outages, solar + battery is essential infrastructure.",
    solarFacts: [
      "Redding: among CA's hottest cities",
      "265 sunny days per year",
      "High PSPS outage frequency",
      "Battery storage critical"
    ]
  },
  {
    name: "Imperial",
    slug: "solar-panels-imperial-county-ca",
    primaryUtility: "SCE",
    population: "180,000",
    majorCities: ["El Centro", "Brawley", "Calexico", "Imperial", "Holtville"],
    avgSunnyDays: 295,
    avgElectricBill: 235,
    metaTitle: "Solar Panels Imperial County 2026: Desert | $ Zero Down | CEAG",
    metaDescription: "Imperial County solar. El Centro, desert region. $Zero down. Cut SCE bills. Free consultation.",
    tldr: "Imperial County has California's most extreme desert climate with 295 sunny days—the state's highest. The 180,000 residents face brutal summer AC costs that solar dramatically reduces.",
    solarFacts: [
      "295 sunny days - California's highest",
      "Extreme desert climate",
      "Massive solar potential",
      "High AC costs = high savings"
    ]
  },
  {
    name: "Humboldt",
    slug: "solar-panels-humboldt-county-ca",
    primaryUtility: "PG&E",
    population: "135,000",
    majorCities: ["Eureka", "Arcata", "Fortuna", "McKinleyville"],
    avgSunnyDays: 210,
    avgElectricBill: 195,
    metaTitle: "Solar Panels Humboldt County 2026: North Coast | $ Zero Down | CEAG",
    metaDescription: "Humboldt County solar. Eureka, Arcata. $Zero down PPA. Beat PG&E rates. Free consultation.",
    tldr: "Humboldt County's 135,000 North Coast residents have fewer sunny days but strong environmental values. Optimized solar + battery still achieves significant savings.",
    solarFacts: [
      "Strong environmental community",
      "HSU: clean energy education",
      "Grid reliability concerns",
      "Battery storage for resilience"
    ]
  },
  {
    name: "Mendocino",
    slug: "solar-panels-mendocino-county-ca",
    primaryUtility: "PG&E",
    population: "90,000",
    majorCities: ["Ukiah", "Fort Bragg", "Willits", "Mendocino"],
    avgSunnyDays: 252,
    avgElectricBill: 225,
    metaTitle: "Solar Panels Mendocino County 2026: Wine Country | $ Zero Down | CEAG",
    metaDescription: "Mendocino County solar. Ukiah, Fort Bragg. $Zero down. Beat PG&E rates. Free consultation.",
    tldr: "Mendocino County's 90,000 residents face extreme PSPS outage risk. Solar + battery is essential infrastructure for the Wine Country and coastal communities.",
    solarFacts: [
      "Extreme PSPS outage area",
      "Wine Country communities",
      "Battery storage essential",
      "Strong environmental values"
    ]
  },
  {
    name: "Lake",
    slug: "solar-panels-lake-county-ca",
    primaryUtility: "PG&E",
    population: "68,000",
    majorCities: ["Clearlake", "Lakeport", "Kelseyville"],
    avgSunnyDays: 267,
    avgElectricBill: 205,
    metaTitle: "Solar Panels Lake County 2026: Clear Lake | $ Zero Down | CEAG",
    metaDescription: "Lake County solar. Clearlake, Lakeport. $Zero down PPA. Beat PG&E. Free consultation.",
    tldr: "Lake County's 68,000 residents around Clear Lake face some of California's most frequent PSPS outages. Solar + battery provides both savings and critical backup power.",
    solarFacts: [
      "Highest PSPS frequency in state",
      "267 sunny days per year",
      "Battery storage absolutely essential",
      "Affordable housing market"
    ]
  },
  {
    name: "Nevada",
    slug: "solar-panels-nevada-county-ca",
    primaryUtility: "PG&E",
    population: "100,000",
    majorCities: ["Grass Valley", "Nevada City", "Truckee", "Penn Valley"],
    avgSunnyDays: 262,
    avgElectricBill: 235,
    metaTitle: "Solar Panels Nevada County 2026: Foothills | $ Zero Down | CEAG",
    metaDescription: "Nevada County solar. Grass Valley, Nevada City, Truckee. $Zero down. Beat PG&E. Free consultation.",
    tldr: "Nevada County's 100,000 foothill residents face frequent PSPS outages and extreme fire risk. Solar + battery is essential for the Grass Valley and mountain communities.",
    solarFacts: [
      "High PSPS outage frequency",
      "Extreme wildfire risk",
      "262 sunny days per year",
      "Environmental communities"
    ]
  },
  {
    name: "Sutter",
    slug: "solar-panels-sutter-county-ca",
    primaryUtility: "PG&E",
    population: "99,000",
    majorCities: ["Yuba City", "Live Oak", "Sutter"],
    avgSunnyDays: 268,
    avgElectricBill: 205,
    metaTitle: "Solar Panels Sutter County 2026: $ Zero Down | CEAG",
    metaDescription: "Sutter County solar. Yuba City area. $Zero down installation. Cut PG&E bills. Free quote.",
    tldr: "Sutter County's 99,000 residents in the Sacramento Valley face hot summers and rising PG&E rates. Solar + battery provides relief with 268 sunny days of production.",
    solarFacts: [
      "Sacramento Valley agricultural hub",
      "268 sunny days annually",
      "Affordable housing market",
      "Hot summers = high AC costs"
    ]
  },
  {
    name: "Yuba",
    slug: "solar-panels-yuba-county-ca",
    primaryUtility: "PG&E",
    population: "80,000",
    majorCities: ["Marysville", "Wheatland", "Olivehurst"],
    avgSunnyDays: 267,
    avgElectricBill: 200,
    metaTitle: "Solar Panels Yuba County 2026: $ Zero Down | CEAG",
    metaDescription: "Yuba County solar. Marysville area. $Zero down PPA. Beat PG&E rates. Free consultation.",
    tldr: "Yuba County's 80,000 residents in the Sacramento Valley experience extreme summer heat. Solar + battery cuts PG&E bills while providing backup during outages.",
    solarFacts: [
      "Sacramento Valley location",
      "267 sunny days per year",
      "Affordable housing market",
      "Growing solar adoption"
    ]
  },
  {
    name: "Tehama",
    slug: "solar-panels-tehama-county-ca",
    primaryUtility: "PG&E",
    population: "65,000",
    majorCities: ["Red Bluff", "Corning", "Tehama"],
    avgSunnyDays: 266,
    avgElectricBill: 195,
    metaTitle: "Solar Panels Tehama County 2026: $ Zero Down | CEAG",
    metaDescription: "Tehama County solar. Red Bluff, Corning. $Zero down installation. Beat PG&E. Free quote.",
    tldr: "Tehama County's 65,000 Northern California residents face extreme summer heat and PSPS outages. Solar + battery provides both savings and essential backup power.",
    solarFacts: [
      "Northern California agricultural hub",
      "266 sunny days annually",
      "Extreme summer heat",
      "PSPS outage risk"
    ]
  },
  {
    name: "Glenn",
    slug: "solar-panels-glenn-county-ca",
    primaryUtility: "PG&E",
    population: "28,000",
    majorCities: ["Willows", "Orland", "Hamilton City"],
    avgSunnyDays: 265,
    avgElectricBill: 190,
    metaTitle: "Solar Panels Glenn County 2026: $ Zero Down | CEAG",
    metaDescription: "Glenn County solar. Willows, Orland. $Zero down PPA. Cut PG&E bills. Free consultation.",
    tldr: "Glenn County's 28,000 Sacramento Valley residents face hot summers and PG&E rate increases. Solar + battery provides affordable energy independence.",
    solarFacts: [
      "Sacramento Valley agricultural area",
      "265 sunny days per year",
      "Affordable housing",
      "Small community going solar"
    ]
  },
  {
    name: "Colusa",
    slug: "solar-panels-colusa-county-ca",
    primaryUtility: "PG&E",
    population: "22,000",
    majorCities: ["Colusa", "Williams", "Arbuckle"],
    avgSunnyDays: 266,
    avgElectricBill: 185,
    metaTitle: "Solar Panels Colusa County 2026: $ Zero Down | CEAG",
    metaDescription: "Colusa County solar. $Zero down installation. Beat PG&E rates. Free quote.",
    tldr: "Colusa County's 22,000 agricultural residents experience Central Valley heat. Solar + battery cuts electricity costs while supporting the farming community.",
    solarFacts: [
      "Agricultural Sacramento Valley",
      "266 sunny days annually",
      "Rice farming community",
      "Affordable solar investment"
    ]
  },
  {
    name: "Calaveras",
    slug: "solar-panels-calaveras-county-ca",
    primaryUtility: "PG&E",
    population: "46,000",
    majorCities: ["Angels Camp", "San Andreas", "Valley Springs"],
    avgSunnyDays: 263,
    avgElectricBill: 225,
    metaTitle: "Solar Panels Calaveras County 2026: Gold Country | $ Zero Down | CEAG",
    metaDescription: "Calaveras County solar. Angels Camp, Gold Country. $Zero down. Beat PG&E. Free consultation.",
    tldr: "Calaveras County's 46,000 Gold Country residents face frequent PSPS outages. Solar + battery provides essential backup and savings in the Sierra foothills.",
    solarFacts: [
      "Gold Country foothills",
      "High PSPS outage frequency",
      "263 sunny days per year",
      "Battery storage essential"
    ]
  },
  {
    name: "Tuolumne",
    slug: "solar-panels-tuolumne-county-ca",
    primaryUtility: "PG&E",
    population: "55,000",
    majorCities: ["Sonora", "Jamestown", "Twain Harte"],
    avgSunnyDays: 264,
    avgElectricBill: 235,
    metaTitle: "Solar Panels Tuolumne County 2026: Yosemite Gateway | $ Zero Down | CEAG",
    metaDescription: "Tuolumne County solar. Sonora, Yosemite area. $Zero down PPA. Beat PG&E. Free consultation.",
    tldr: "Tuolumne County's 55,000 residents at the gateway to Yosemite face extreme PSPS outage risk. Solar + battery is essential infrastructure for these mountain communities.",
    solarFacts: [
      "Gateway to Yosemite",
      "Extreme PSPS outage area",
      "264 sunny days per year",
      "Battery storage critical"
    ]
  },
  {
    name: "Amador",
    slug: "solar-panels-amador-county-ca",
    primaryUtility: "PG&E",
    population: "41,000",
    majorCities: ["Jackson", "Sutter Creek", "Ione", "Plymouth"],
    avgSunnyDays: 262,
    avgElectricBill: 220,
    metaTitle: "Solar Panels Amador County 2026: Gold Country | $ Zero Down | CEAG",
    metaDescription: "Amador County solar. Jackson, Sutter Creek. $Zero down. Beat PG&E. Free consultation.",
    tldr: "Amador County's 41,000 Gold Country residents face frequent PSPS outages and high fire risk. Solar + battery provides essential backup power and savings.",
    solarFacts: [
      "Historic Gold Country",
      "High PSPS frequency",
      "262 sunny days annually",
      "Wine country communities"
    ]
  },
  {
    name: "San Benito",
    slug: "solar-panels-san-benito-county-ca",
    primaryUtility: "PG&E",
    population: "65,000",
    majorCities: ["Hollister", "San Juan Bautista"],
    avgSunnyDays: 268,
    avgElectricBill: 235,
    metaTitle: "Solar Panels San Benito County 2026: $ Zero Down | CEAG",
    metaDescription: "San Benito County solar. Hollister area. $Zero down PPA. Cut PG&E bills. Free consultation.",
    tldr: "San Benito County's 65,000 residents in Hollister and San Juan Bautista are escaping PG&E's rising rates. Solar + battery provides independence with 268 sunny days.",
    solarFacts: [
      "Growing South Bay commuter community",
      "268 sunny days per year",
      "Affordable compared to Silicon Valley",
      "PG&E rates rising rapidly"
    ]
  },
  {
    name: "Inyo",
    slug: "solar-panels-inyo-county-ca",
    primaryUtility: "SCE",
    population: "19,000",
    majorCities: ["Bishop", "Lone Pine", "Big Pine"],
    avgSunnyDays: 290,
    avgElectricBill: 215,
    metaTitle: "Solar Panels Inyo County 2026: Eastern Sierra | $ Zero Down | CEAG",
    metaDescription: "Inyo County solar. Bishop, Lone Pine. $Zero down. Cut SCE bills. Free consultation.",
    tldr: "Inyo County's 19,000 Eastern Sierra residents enjoy 290 sunny days—among California's best. Desert conditions and SCE rates make solar + battery ideal.",
    solarFacts: [
      "290 sunny days - exceptional",
      "Eastern Sierra desert climate",
      "Death Valley region",
      "Remote communities need battery backup"
    ]
  },
  {
    name: "Mono",
    slug: "solar-panels-mono-county-ca",
    primaryUtility: "SCE",
    population: "14,000",
    majorCities: ["Mammoth Lakes", "Lee Vining", "Bridgeport"],
    avgSunnyDays: 275,
    avgElectricBill: 225,
    metaTitle: "Solar Panels Mono County 2026: Eastern Sierra | $ Zero Down | CEAG",
    metaDescription: "Mono County solar. Mammoth Lakes. $Zero down PPA. Cut SCE bills. Free consultation.",
    tldr: "Mono County's 14,000 Eastern Sierra residents face unique high-altitude conditions. Solar + battery provides energy independence for Mammoth Lakes and mountain communities.",
    solarFacts: [
      "Mammoth Lakes resort community",
      "275 sunny days per year",
      "High-altitude solar production",
      "Grid reliability concerns"
    ]
  },
  {
    name: "Alpine",
    slug: "solar-panels-alpine-county-ca",
    primaryUtility: "PG&E",
    population: "1,200",
    majorCities: ["Markleeville", "Bear Valley"],
    avgSunnyDays: 260,
    avgElectricBill: 215,
    metaTitle: "Solar Panels Alpine County 2026: Sierra | $ Zero Down | CEAG",
    metaDescription: "Alpine County solar. Markleeville. $Zero down. Beat PG&E. Free consultation.",
    tldr: "Alpine County, California's least populous county, faces extreme weather and grid reliability issues. Solar + battery provides essential energy independence.",
    solarFacts: [
      "California's least populous county",
      "260 sunny days per year",
      "Remote mountain location",
      "Battery storage essential"
    ]
  },
  {
    name: "Sierra",
    slug: "solar-panels-sierra-county-ca",
    primaryUtility: "PG&E",
    population: "3,200",
    majorCities: ["Loyalton", "Downieville", "Sierra City"],
    avgSunnyDays: 258,
    avgElectricBill: 205,
    metaTitle: "Solar Panels Sierra County 2026: $ Zero Down | CEAG",
    metaDescription: "Sierra County solar. $Zero down PPA. Beat PG&E rates. Free consultation.",
    tldr: "Sierra County's 3,200 mountain residents face grid reliability challenges and PSPS outages. Solar + battery provides essential backup power.",
    solarFacts: [
      "Small mountain communities",
      "258 sunny days per year",
      "High PSPS frequency",
      "Battery storage critical"
    ]
  },
  {
    name: "Plumas",
    slug: "solar-panels-plumas-county-ca",
    primaryUtility: "PG&E",
    population: "19,000",
    majorCities: ["Quincy", "Portola", "Chester"],
    avgSunnyDays: 256,
    avgElectricBill: 210,
    metaTitle: "Solar Panels Plumas County 2026: $ Zero Down | CEAG",
    metaDescription: "Plumas County solar. Quincy area. $Zero down. Beat PG&E. Free consultation.",
    tldr: "Plumas County's 19,000 mountain residents face extreme PSPS outage risk and devastating wildfires. Solar + battery provides essential resilience.",
    solarFacts: [
      "Extreme wildfire area",
      "256 sunny days per year",
      "High PSPS frequency",
      "Rebuilding communities need solar"
    ]
  },
  {
    name: "Lassen",
    slug: "solar-panels-lassen-county-ca",
    primaryUtility: "PG&E",
    population: "31,000",
    majorCities: ["Susanville", "Westwood", "Janesville"],
    avgSunnyDays: 255,
    avgElectricBill: 195,
    metaTitle: "Solar Panels Lassen County 2026: $ Zero Down | CEAG",
    metaDescription: "Lassen County solar. Susanville. $Zero down PPA. Beat PG&E. Free consultation.",
    tldr: "Lassen County's 31,000 Northeastern California residents face harsh winters and grid reliability issues. Solar + battery provides year-round energy independence.",
    solarFacts: [
      "Northeastern California",
      "255 sunny days per year",
      "Remote location challenges",
      "Battery storage important"
    ]
  },
  {
    name: "Modoc",
    slug: "solar-panels-modoc-county-ca",
    primaryUtility: "PG&E",
    population: "9,000",
    majorCities: ["Alturas", "Cedarville", "Tulelake"],
    avgSunnyDays: 254,
    avgElectricBill: 185,
    metaTitle: "Solar Panels Modoc County 2026: $ Zero Down | CEAG",
    metaDescription: "Modoc County solar. Alturas. $Zero down. Beat PG&E rates. Free consultation.",
    tldr: "Modoc County's 9,000 remote Northeastern California residents face grid reliability challenges. Solar + battery provides essential energy independence.",
    solarFacts: [
      "Most remote California county",
      "254 sunny days per year",
      "Grid reliability concerns",
      "Battery backup essential"
    ]
  },
  {
    name: "Siskiyou",
    slug: "solar-panels-siskiyou-county-ca",
    primaryUtility: "PG&E",
    population: "44,000",
    majorCities: ["Yreka", "Mt. Shasta", "Weed", "Dunsmuir"],
    avgSunnyDays: 252,
    avgElectricBill: 205,
    metaTitle: "Solar Panels Siskiyou County 2026: Mt. Shasta | $ Zero Down | CEAG",
    metaDescription: "Siskiyou County solar. Mt. Shasta, Yreka. $Zero down. Beat PG&E. Free consultation.",
    tldr: "Siskiyou County's 44,000 Northern California residents face extreme PSPS outage risk and wildfire danger. Solar + battery is essential infrastructure.",
    solarFacts: [
      "Mt. Shasta communities",
      "252 sunny days per year",
      "High PSPS and fire risk",
      "Battery storage critical"
    ]
  },
  {
    name: "Trinity",
    slug: "solar-panels-trinity-county-ca",
    primaryUtility: "PG&E",
    population: "16,000",
    majorCities: ["Weaverville", "Hayfork", "Lewiston"],
    avgSunnyDays: 250,
    avgElectricBill: 195,
    metaTitle: "Solar Panels Trinity County 2026: $ Zero Down | CEAG",
    metaDescription: "Trinity County solar. Weaverville. $Zero down PPA. Beat PG&E. Free consultation.",
    tldr: "Trinity County's 16,000 remote mountain residents face extreme PSPS outage frequency and limited grid access. Solar + battery provides essential independence.",
    solarFacts: [
      "Remote mountain communities",
      "250 sunny days per year",
      "Extreme PSPS area",
      "Battery storage essential"
    ]
  },
  {
    name: "Del Norte",
    slug: "solar-panels-del-norte-county-ca",
    primaryUtility: "PG&E",
    population: "28,000",
    majorCities: ["Crescent City", "Smith River"],
    avgSunnyDays: 200,
    avgElectricBill: 190,
    metaTitle: "Solar Panels Del Norte County 2026: $ Zero Down | CEAG",
    metaDescription: "Del Norte County solar. Crescent City. $Zero down. Beat PG&E. Free consultation.",
    tldr: "Del Norte County's 28,000 coastal residents face grid reliability challenges. Solar + battery provides essential backup power for California's northwest corner.",
    solarFacts: [
      "California's northwest corner",
      "200 sunny days per year",
      "Grid reliability concerns",
      "Battery storage important"
    ]
  }
];

// Get county by slug
export const getCountyBySlug = (slug: string): CaliforniaCounty | undefined => {
  return californiaCounties.find(county => county.slug === slug);
};

// Get counties by utility
export const getCountiesByUtility = (utility: string): CaliforniaCounty[] => {
  return californiaCounties.filter(county => county.primaryUtility === utility);
};

// Get county slugs for routing
export const getCountySlugs = (): string[] => {
  return californiaCounties.map(county => county.slug);
};

// Get cities in county (from californiaData.ts)
export const getCitiesInCounty = (countyName: string, cities: { county: string; slug: string; name: string }[]): { slug: string; name: string }[] => {
  return cities.filter(city => city.county === `${countyName} County`).map(city => ({ slug: city.slug, name: city.name }));
};
